// Email Signature API - API integration for email signatures
// Uses ApiClient with dependency injection pattern

import type { ApiClient } from '@commander/api-client';
import type { EmailSignature } from '../types';
import type { PaginationMeta } from './common';

// Backend response types (snake_case from API)
interface BackendEmailSignature {
  id: string;
  user_id: string;
  name: string;
  body?: any; // Lexical JSON (only present in details response)
  content_type: 'lexical' | 'html';
  html_body?: string; // Raw HTML body (when content_type === 'html')
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface BackendSignatureListItem {
  id: string;
  user_id: string;
  name: string;
  content_type: 'lexical' | 'html';
  is_default: boolean;
  created_at: string;
  updated_at: string;
  // Note: 'body' and 'html_body' fields are NOT included in list response
}

// Request types (snake_case for API)
export interface EmailSignatureListParams {
  page?: number;
  limit?: number;
  name?: string;
}

export interface EmailSignatureCreateData {
  name: string;
  body?: any; // Lexical JSON body (required when content_type === 'lexical')
  content_type?: 'lexical' | 'html';
  html_body?: string; // Raw HTML body (required when content_type === 'html')
  is_default?: boolean;
}

export interface EmailSignatureUpdateData {
  name?: string;
  body?: any; // Lexical JSON body
  content_type?: 'lexical' | 'html';
  html_body?: string; // Raw HTML body
  is_default?: boolean;
}

// Transform list item (no body field)
function transformSignatureListItem(backend: BackendSignatureListItem): EmailSignature {
  return {
    id: backend.id,
    userId: backend.user_id,
    name: backend.name,
    html: '', // Not available in list response
    text: '',
    contentType: backend.content_type || 'lexical',
    isDefault: backend.is_default,
    createdAt: new Date(backend.created_at),
    updatedAt: new Date(backend.updated_at),
  };
}

// Transform full signature (with Lexical body from details endpoint)
function transformSignature(backend: BackendEmailSignature): EmailSignature {
  return {
    id: backend.id,
    userId: backend.user_id,
    name: backend.name,
    html: '', // Generated by Editor's onHtmlChange callback (not stored)
    text: '',
    lexicalBody: backend.body, // Store Lexical JSON for editing
    contentType: backend.content_type || 'lexical',
    htmlBody: backend.html_body,
    isDefault: backend.is_default,
    createdAt: new Date(backend.created_at),
    updatedAt: new Date(backend.updated_at),
  };
}

/**
 * Factory function to create email signature API service with ApiClient dependency injection
 * @param apiClient - ApiClient instance from context
 * @returns Service object with all email signature API methods
 */
export const createEmailSignatureApi = (apiClient: ApiClient) => ({
  /**
   * Get paginated list of email signatures with filtering
   * GET /communications/email_signatures
   * Note: List response does NOT include 'body' field (only id, name, timestamps)
   */
  async getEmailSignatures(params?: EmailSignatureListParams) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.name) queryParams.append('name', params.name);

    const endpoint = `/communications/email_signatures${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get<{
      data: BackendSignatureListItem[];
      meta: PaginationMeta;
    }>(endpoint);

    return {
      signatures: response.data.map(transformSignatureListItem),
      pagination: response.meta,
    };
  },

  /**
   * Get a single email signature by ID
   * GET /communications/email_signatures/:id
   * Note: Details response INCLUDES 'body' field with Lexical JSON
   */
  async getEmailSignature(signatureId: string) {
    const response = await apiClient.get<{ data: BackendEmailSignature }>(
      `/communications/email_signatures/${signatureId}`
    );
    return transformSignature(response.data);
  },

  /**
   * Create a new email signature
   * POST /communications/email_signatures
   */
  async createEmailSignature(data: EmailSignatureCreateData) {
    const response = await apiClient.post<{ data: BackendEmailSignature }>(
      '/communications/email_signatures',
      data
    );
    return transformSignature(response.data);
  },

  /**
   * Update an existing email signature
   * PUT /communications/email_signatures/:id
   */
  async updateEmailSignature(signatureId: string, data: EmailSignatureUpdateData) {
    const response = await apiClient.put<{ data: BackendEmailSignature }>(
      `/communications/email_signatures/${signatureId}`,
      data
    );
    return transformSignature(response.data);
  },

  /**
   * Delete an email signature (soft delete)
   * DELETE /communications/email_signatures/:id
   */
  async deleteEmailSignature(signatureId: string): Promise<void> {
    await apiClient.delete<void>(`/communications/email_signatures/${signatureId}`);
  },
});

// Type for the service instance
export type EmailSignatureApi = ReturnType<typeof createEmailSignatureApi>;
